import { useEffect, useRef } from 'react'
import {
  usePRFileAllFrames,
  usePRFileSections,
  useShowSingleEntity,
} from '@sketch-hq/sketch-web-renderer'

import { useFlag } from '@sketch/modules-common'

import { useCanvasScope } from '../scope'
import { CanvasCameraPosition } from './useRestoreCanvasCamera'

/** How far the rest of the page fades back behind the focused entity. */
const UNFOCUSED_OPACITY = 0.15

/**
 * Whether one of the renderer's entity lists holds this entity. A list is null
 * until the file it comes from has been parsed.
 */
const holds = (entities: { objectId: string }[] | null, entityUUID: string) =>
  !!entities?.some(({ objectId }) => objectId === entityUUID)

/**
 * A remembered position as a camera state, or undefined if there isn't a whole
 * one - half a position says nothing about where to end up.
 */
const toCameraState = (position?: CanvasCameraPosition | null) => {
  const { initialZoom, initialPan } = position ?? {}

  if (initialZoom === undefined || initialPan === undefined) return undefined

  return { zoom: initialZoom, pan: { x: initialPan.x, y: initialPan.y } }
}

/** What was last asked of the renderer, so that a repeat can be recognised. */
interface AppliedFocus {
  pageUUID?: string
  entityUUID: string | null
  /** Whether it was picked out by fading rather than by hiding. */
  isFaded: boolean
}

/**
 * Focuses an entity in the renderer.
 *
 * Behind `canvas-focus-animation`, the rest of the page fades back and the
 * camera travels to the entity. Nothing is re-rooted or hidden, which is what
 * lets the move happen without remounting the canvas - the stored camera
 * position is read only at init (see useInitialCameraPosition). The renderer
 * eases out of the move if you touch the canvas mid-flight, and skips it under
 * `prefers-reduced-motion`.
 *
 * With the flag off this does what it always did, and only for a frame: the
 * renderer hides the rest of the page and cuts to it. A Section is isolated by
 * being the scene root instead (see SharePageCanvas), because the runtime
 * isolation only knows the page's own children - asking it for an entity
 * nested deeper hides every one of them and draws nothing.
 *
 * The flag is read on each focus change rather than watched: turning it off
 * mid-view leaves the fade already on screen, because hiding does not clear it.
 *
 * Where the move ends is the entity's own remembered position when it has one,
 * and the fit of the entity otherwise - so returning to a frame travels back
 * to where it was left rather than reframing it. Without the treatment that is
 * `useRestoreCanvasCamera`'s job, which puts the position back over the fit;
 * the move cannot be corrected that way, because a snap does not cancel a
 * camera animation, so it is given the destination up front instead.
 *
 * Nothing is asked of the renderer until it can resolve the entity - see
 * `canFocus` below.
 *
 * @param lastCameraPosition Where the entity being focused was last left,
 * already scoped to it by the caller.
 */
export function useFocusCanvasEntity(
  lastCameraPosition?: CanvasCameraPosition | null
) {
  const scope = useCanvasScope()
  const [rendererFocusedUUID, setShowSingleEntity] = useShowSingleEntity()
  const isFocusAnimationEnabled = useFlag('canvas-focus-animation')

  // With the treatment on, a frame and a Section are picked out the same way:
  // by fading the rest of the page. Without it, only a frame is isolated here
  // and a Section is left to the scene root, as it has always been.
  const focusedEntity =
    scope && scope.focus.kind !== 'page' ? scope.focus : null

  const isIsolatedHere =
    !!focusedEntity &&
    (isFocusAnimationEnabled || focusedEntity.kind === 'frame')

  const focusedEntityUUID = isIsolatedHere ? focusedEntity.uuid : null
  const pageUUID = scope?.pageUUID

  const fileFrames = usePRFileAllFrames()
  const fileSections = usePRFileSections()

  /**
   * Whether there is a renderer able to act on this focus yet.
   *
   * The canvas is mounted above the view, so the first run of this effect
   * happens before the provider has built a renderer at all - and a call made
   * then reaches nothing, because there is no instance to take it. The
   * renderer remembers a focus asked for before its canvas is up, but it can
   * only do that once it exists.
   *
   * Waiting on the entity rather than on the instance covers the rest of the
   * same problem: the lists are null until the file is parsed, and are rebuilt
   * as fragments arrive, so this also waits for a frame nested inside a
   * Section, which appears only once that fragment has landed.
   */
  const canFocus =
    focusedEntityUUID === null ||
    holds(fileFrames, focusedEntityUUID) ||
    holds(fileSections, focusedEntityUUID)

  // Read when the move is made rather than depended on: it is recomputed from
  // local storage every render, and a new object must not re-run the effect.
  const cameraRef = useRef(lastCameraPosition)
  cameraRef.current = lastCameraPosition

  const applied = useRef<AppliedFocus | null>(null)

  useEffect(() => {
    if (!canFocus) {
      // A file being rebuilt takes the renderer's focus with it, so what was
      // applied to the old one no longer describes it. Forgetting here is what
      // makes the focus be applied again once the new file resolves.
      applied.current = null
      return
    }

    const previous = applied.current
    const next: AppliedFocus = {
      pageUUID,
      entityUUID: focusedEntityUUID,
      isFaded: isFocusAnimationEnabled,
    }

    // Turning the flag over changes how the same entity is picked out, so the
    // renderer has to be told again even though nothing about the focus
    // itself moved.
    const hasSwitchedTreatment = !!previous && previous.isFaded !== next.isFaded

    // `setShowSingleEntity` is rebuilt whenever the renderer's frame and
    // Section lists change, which they do repeatedly as a file loads. Without
    // this the effect would re-run and re-fit the camera while nothing had
    // actually changed, throwing away wherever the reader had panned to.
    const isUnchanged =
      !!previous &&
      previous.pageUUID === next.pageUUID &&
      previous.entityUUID === next.entityUUID &&
      !hasSwitchedTreatment

    if (isUnchanged) return

    applied.current = next

    // Nothing to say to a renderer already focused on this. Setting it fits
    // the camera, and the first run on the page canvas would otherwise do
    // exactly that for no change at all - focus is null there, and so is the
    // renderer - throwing away the position the canvas was initialized at,
    // which is the one a "Copy Link from Position" link carries (WEB-3968).
    //
    // A switched treatment is the exception: the entity is the same one the
    // renderer reports, and it still has to be picked out differently.
    if (!hasSwitchedTreatment && rendererFocusedUUID === focusedEntityUUID) {
      return
    }

    if (!isFocusAnimationEnabled) {
      setShowSingleEntity(focusedEntityUUID)
      return
    }

    // Only worth animating within a page. Travelling is only legible across a
    // document you can already see: another page is another file, so there is
    // nothing continuous to move through, and easing from the previous page's
    // camera reads as drift. The first focus of all is a cut for the same
    // reason - there is nothing to move from.
    const isSamePage = !!previous && previous.pageUUID === next.pageUUID

    setShowSingleEntity(focusedEntityUUID, {
      animate: isSamePage,
      dim: UNFOCUSED_OPACITY,
      camera: toCameraState(cameraRef.current),
    })
  }, [
    canFocus,
    focusedEntityUUID,
    isFocusAnimationEnabled,
    rendererFocusedUUID,
    pageUUID,
    setShowSingleEntity,
  ])
}
